import { z } from "zod";

import { canViewFinancials } from "../platform/access-control";
import { APP_PATHS } from "../platform/app-metadata";
import { ORDER_PRICE_FILTER_ENABLED, ORDER_PRICE_FILTER_ERRORS } from "./order-price-filter-config";
import type { OrderDirection, OrderStatus } from "./order-types";
import { MISSING_PRICE_VIEW, orderMatchesView } from "./order-views";

export { ORDER_PRICE_FILTER_ENABLED, ORDER_PRICE_FILTER_ERRORS } from "./order-price-filter-config";
export type OrderPriceFacts = { pricePerItem?: number | string | null; orderStatus: OrderStatus; direction: OrderDirection };

const filterSchema = z.object({
  missingPrice: z.literal("true").optional(),
  scope: z.never().optional(), start: z.never().optional(), end: z.never().optional(),
});
export type OrderPriceFilter = Pick<z.infer<typeof filterSchema>, "missingPrice">;

function queryObject(query: URLSearchParams): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of query.keys()) {
    const values = query.getAll(key);
    result[key] = values.length === 1 ? values[0] : values;
  }
  return result;
}

export function parseOrderPriceFilter(input: unknown) {
  return filterSchema.safeParse(input instanceof URLSearchParams ? queryObject(input) : input);
}

export function orderPriceFilterQuery(filter: OrderPriceFilter): string {
  return filter.missingPrice ? new URLSearchParams({ missingPrice: filter.missingPrice }).toString() : "";
}

export function buildOrderPriceFilterHref(): string | null {
  return ORDER_PRICE_FILTER_ENABLED ? `${APP_PATHS.orders}?view=${MISSING_PRICE_VIEW}` : null;
}

export function orderPriceFilterAccessError(filter: OrderPriceFilter, role: string | null | undefined) {
  if (!filter.missingPrice) return null;
  if (!canViewFinancials(role)) return { status: 403, message: ORDER_PRICE_FILTER_ERRORS.forbidden };
  return ORDER_PRICE_FILTER_ENABLED ? null : { status: 503, message: ORDER_PRICE_FILTER_ERRORS.disabled };
}

function matchesMissingPrice(order: OrderPriceFacts): boolean {
  return orderMatchesView({ status: order.orderStatus, direction: order.direction, pricePerItem: order.pricePerItem }, MISSING_PRICE_VIEW);
}

export function selectOrdersByPriceFilter<T extends OrderPriceFacts>(orders: readonly T[], filter: OrderPriceFilter): T[] {
  return filter.missingPrice ? orders.filter(matchesMissingPrice) : [...orders];
}
